import { FC, useMemo } from 'react';
import { TrendsWidget } from '../dashboard/TrendsWidget';
import { useActiveDirectoryAccessContext } from '../../context';
import { StaleResourcesWidget } from './dashboard/StaleResourcesWidget';
import { CHART_COLORS } from '@silverfort-nodelibs/common/utils/consts';
import { HexColor } from '@silverfort/sf-components';
import { AccessIntelligenceGaugeCarouselWidget } from '../dashboard/AccessIntelligenceGaugeCarouselWidget';
import { DaysRange, DaysRangeLabels } from '../../types/DaysRange';
import { GaugeChartCarouselProps } from '../../types/GaugeChartProps';

const calculateRatio = (total: number, value?: number) => {
    const ratio = total ? ((value ?? 0) / total) * 100 : 0;

    if (ratio === 0) return 0;

    return ratio < 1 ? 1 : Math.round(ratio);
};

export const Dashboard: FC = () => {
    const {
        hasPolicyEnabled,
        selectedStaleResourcesDaysRange,
        setSelectedStaleResourcesDaysRange,
        snapshot,
        snapshotLoading
    } = useActiveDirectoryAccessContext();

    const hosts = useMemo<Array<GaugeChartCarouselProps>>(() => {
        if (!hasPolicyEnabled) return [];

        return [
            {
                endColor: CHART_COLORS[7] as HexColor,
                startColor: CHART_COLORS[3] as HexColor,
                value: calculateRatio(snapshot.hosts.total, snapshot.hosts.total_mfa_enforcements)
            }
        ];
    }, [hasPolicyEnabled, snapshot]);

    const staleResources = useMemo<Array<GaugeChartCarouselProps & { id: DaysRange; label: string }>>(
        () =>
            snapshot.resources.period_metrics.map((metric) => ({
                endColor: CHART_COLORS[4] as HexColor,
                id: metric.days_range,
                label: DaysRangeLabels[metric.days_range],
                startColor: CHART_COLORS[1] as HexColor,
                value: calculateRatio(snapshot.resources.total, metric.total)
            })),
        [snapshot]
    );

    const trends = useMemo<Array<{ title: string; total: number }>>(
        () => [
            {
                title: 'Monitored application',
                total: snapshot.applications.total ?? 0
            },
            {
                title: 'Identities',
                total: snapshot.users.total ?? 0
            },
            {
                title: 'Resources',
                total: snapshot.resources.total ?? 0
            },
            {
                title: 'Stale Access Users',
                total: snapshot.inactive_users.total ?? 0
            }
        ],
        [snapshot]
    );

    return (
        <section className="w-full flex gap-4">
            <TrendsWidget loading={snapshotLoading} trends={trends} />

            {hasPolicyEnabled && (
                <AccessIntelligenceGaugeCarouselWidget
                    chartLabel="Covered by Silverfort"
                    charts={hosts}
                    loading={snapshotLoading}
                    widgetTitle="Hosts Protection Coverage"
                />
            )}

            <StaleResourcesWidget
                chartLabel="Application Hosts"
                charts={staleResources}
                loading={snapshotLoading}
                selectedDaysRange={selectedStaleResourcesDaysRange}
                setSelectedDaysRange={setSelectedStaleResourcesDaysRange}
                widgetTitle="Stale Resources"
            />
        </section>
    );
};
